import React from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  Skeleton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material'
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend
} from 'recharts'
import {
  CheckCircle,
  AttachMoney,
  Warning
} from '@mui/icons-material'
import { formatCurrency } from '../../utils/formatters'

const StatusPieChart = ({ data, loading, title = "Distribución por Estado" }) => {
  const theme = useTheme()

  const statusColors = {
    PAGADA: theme.palette.success.main,
    POR_COBRAR: theme.palette.warning.main,
    VENCIDA: theme.palette.error.main
  }

  const statusLabels = {
    PAGADA: 'Pagadas',
    POR_COBRAR: 'Por Cobrar',
    VENCIDA: 'Vencidas'
  }

  const statusIcons = {
    PAGADA: <CheckCircle />,
    POR_COBRAR: <AttachMoney />,
    VENCIDA: <Warning />
  }

  const formatTooltip = (value, name) => {
    return [formatCurrency(value), statusLabels[name] || name]
  }

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null // Don't show labels for slices less than 5%
    
    const RADIAN = Math.PI / 180
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="12"
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    )
  }

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto' }} />
        </CardContent>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Box 
            sx={{ 
              flexGrow: 1,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No hay datos disponibles
            </Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  const chartData = data.map(item => ({
    name: item.status,
    value: item.amount,
    count: item.count
  }))

  const totalAmount = data.reduce((sum, item) => sum + item.amount, 0)

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1 }}>
          {/* Pie Chart */}
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Box sx={{ width: 240, height: 240 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={100}
                    innerRadius={35}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="#ffffff"
                    strokeWidth={3}
                  >
                    {chartData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={statusColors[entry.name] || theme.palette.grey[500]} 
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={formatTooltip} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          {/* Legend */}
          <Box sx={{ px: 1 }}>
            <List dense>
              {data.map((item) => (
                <ListItem key={item.status} sx={{ py: 1, px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Box 
                      sx={{ 
                        color: statusColors[item.status],
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {statusIcons[item.status]}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={`${statusLabels[item.status]} - ${item.count} facturas`}
                    secondary={`${formatCurrency(item.amount)} (${totalAmount > 0 ? ((item.amount / totalAmount) * 100).toFixed(1) : 0}%)`}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: 600
                    }}
                    secondaryTypographyProps={{
                      variant: "body2",
                      fontWeight: 500,
                      color: "primary.main"
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default StatusPieChart
